import { Link } from 'react-router-dom';
import { ArrowDownRightIcon, ArrowUpRightIcon, DownloadIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Tabs } from '../ui/Tabs';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { Spinner } from '../../ui/Spinner';
import { btnSecondary, fadeWhileLoading } from '../ui/styles';
import { SalesChart } from './SalesChart';
import { PeriodBar } from './PeriodBar';
import { useSales } from './useSales';
import { change, changeLabel, rangeLabel } from './salesPeriods';
import { ADMIN_BASE } from '../../utils/hosts';
import { money } from '../../core/format/currency';
import { channelLabel, receivedMethodLabel } from '../../core/rules/revenue';
import { ChannelSplit, StockPeriodPanel } from './ChannelPanels';
import { LiabilitiesPanel } from './LiabilitiesPanel';
import { PerformerPanel } from './PerformerPanel';
import { StaffHoursPanel } from './StaffHoursPanel';
import { useAdmin } from '../AdminContext';
import { staffList } from '../../core/rules/salonDays';
import { API_BASE } from '../../core/api/http';
import type { SalesChannel } from '../../core/types/adminRequests';
const channelTabs: {
  key: SalesChannel | 'alle';
  label: string;
}[] = [{
  key: 'alle',
  label: 'Alle kanalen'
}, {
  key: 'webshop',
  label: 'Webshop'
}, {
  key: 'salon',
  label: 'Salon'
}];

/**
 * Omzet & rapporten, webshop en salon (BEHEER-PLAN §3.5), met een filter op kanaal. Eén groot getal:
 * omzet exclusief btw, met het verschil t.o.v. dezelfde dagen in de vorige periode. Alles wat
 * hier staat rekent de server; het scherm telt niets zelf op.
 */
export function Sales() {
  const sales = useSales();
  const {
    from,
    to,
    groupBy,
    previous,
    channel,
    setChannel,
    report,
    status,
    reload,
    exportCsv,
    exporting,
    query
  } = sales;
  const scope = channel ? channelLabel[channel].toLowerCase() : 'webshop en salon';
  const {
    site
  } = useAdmin();
  const hasColleagues = staffList(site.staff).length > 1;
  return <div data-id="element-3856" data-mp="3trsed8a42d2r">
      <PageHeader title="Omzet" description="Bestellingen op de dag van betalen, bonnen op de dag van afrekenen, terugbetalingen op de dag van terugbetalen." actions={<button type="button" onClick={() => void exportCsv('omzet')} disabled={exporting} className={`${btnSecondary} disabled:cursor-wait disabled:opacity-60`} data-id="element-3858" data-mp="6mujxog0pjje">
            {exporting ? <Spinner data-id="element-3859" data-mp="3b1kldj04l30a" /> : <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3860" data-mp="3b35dz32vqbvi" />}
            {exporting ? 'Exporteren…' : 'Exporteer CSV'}
          </button>} data-id="element-3857" data-mp="3trac9v0rp4mx" />

      <PeriodBar state={sales} data-id="element-3861" data-mp="3juxk51kveyua">
        <Tabs variant="pill" label="Kanaal" tabs={channelTabs} value={channel ?? 'alle'} onChange={setChannel} data-id="element-3862" data-mp="3jw8lyvur9wir" />
      </PeriodBar>

      {status === 'error' && !report ? <LoadFailed title="De omzet kon niet laden" onRetry={reload} data-id="element-3863" data-mp="2rdlam85k9vuv" /> : !report ? <div role="status" aria-label="Omzet laden" className="space-y-6" data-id="element-3864" data-mp="2ndku8hywjal6">
          <Skeleton className="h-28 w-72 rounded-lg" data-id="element-3865" data-mp="2d94f5qt8xxif" />
          <Skeleton className="h-52 w-full rounded-lg" data-id="element-3866" data-mp="2d79v0epsu9fe" />
        </div> : <div className={`space-y-6 ${fadeWhileLoading(status === 'loading')}`} aria-busy={status === 'loading'} data-id="element-3867" data-mp="2i0md05zz3hz6">
          <section aria-labelledby="omzet-hero" className="rounded-lg border border-line bg-white p-5 sm:p-7" data-id="element-3868" data-mp="23c85hhrm35ca">
            <h2 id="omzet-hero" className="text-[13px] text-muted" data-id="element-3869" data-mp="28a1860tphh7t">
              Omzet excl. btw, {scope} · {rangeLabel(from, to)}
            </h2>
            <p className="mt-1 font-display text-[2.6rem] font-light leading-none tabular-nums text-ink sm:text-[3.4rem]" data-id="element-3870" data-mp="30yypo1q3f9zi">
              {money(report.totals.revenueExVatCents)}
            </p>
            <Delta current={report.totals.revenueExVatCents} previous={report.previous.revenueExVatCents} label={`t.o.v. ${rangeLabel(previous.from, previous.to)}`} data-id="element-3871" data-mp="2qzpzljycmfif" />

            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-line pt-5 sm:grid-cols-4 xl:grid-cols-7" data-id="element-3872" data-mp="1aoc9kf9v9hgk">
              <Stat label="Verkopen" value={String(report.totals.orders)} data-id="element-3873" data-mp="1vryvqh096q36" />
              <Stat label="Gemiddeld per verkoop" value={money(report.totals.averageOrderCents)} data-id="element-3874" data-mp="1vu1upqjdvtk1" />
              <Stat label="Btw" value={money(report.totals.vatCents)} data-id="element-3875" data-mp="1qznu99pv6w8i" />
              <Stat label="Terugbetaald" value={report.totals.refundsCents ? `−${money(report.totals.refundsCents)}` : money(0)} data-id="element-3876" data-mp="1m2xcmhyevskz" />
              <Stat label="Waarvan behandelingen" value={money(report.totals.treatmentsExVatCents)} hint="Salon en kuren" data-id="element-3877" data-mp="1h1hw9376ia8x" />
              <Stat label="Waarvan verzending" value={money(report.totals.shippingExVatCents)} data-id="element-3878" data-mp="29rgof7go6nht" />
              <Stat label="Cadeaubonnen verkocht" value={money(report.totals.giftCardsCents)} hint="Geen omzet: btw pas bij inwisselen" data-id="element-3879" data-mp="24sdsqdmkxtmi" />
            </dl>
          </section>

          <Panel title="Verloop" description={`Omzet excl. btw per ${groupBy === 'day' ? 'dag' : groupBy === 'week' ? 'week' : 'maand'}, ${scope}`} data-id="element-3880" data-mp="1zw6ii8n5hvyr">
            <SalesChart series={report.series} groupBy={groupBy} data-id="element-3881" data-mp="odlx7ru5068z" />
          </Panel>

          {/* Een oudere server stuurt geen kanalen mee: dan geen leeg blok. */}
          {report.byChannel.length > 0 && <ChannelSplit rows={report.byChannel} totalCents={report.byChannel.reduce((s, c) => s + c.revenueExVatCents, 0)} data-id="element-3882" data-mp="351myfhbitx2t" />}

          {/* Alleen met een collega en niet in de webshopweergave: de webshop heeft geen uitvoerder. */}
          {hasColleagues && channel !== 'webshop' && report.byPerformer && report.byPerformer.length > 0 && <PerformerPanel rows={report.byPerformer} team={staffList(site.staff)} data-id="element-3883" data-mp="3nrbgway6rl1a" />}

          <div className="grid gap-6 lg:grid-cols-3" data-id="element-3884" data-mp="3sqxtci66wx91">
            <Panel title="Best verkocht" description="Top 10 op omzet excl. btw, min terugbetalingen" flush className="lg:col-span-2" data-id="element-3885" data-mp="3srh9btca5bkf">
              {report.topProducts.length === 0 ? <p className="px-4 py-8 text-center text-[13px] text-muted sm:px-5" data-id="element-3886" data-mp="1ye7vxqgm55ww">Geen verkopen in deze periode.</p> : <table className="w-full text-[14px]" data-id="element-3887" data-mp="1vb239ii06naw">
                  <caption className="sr-only" data-id="element-3888" data-mp="1vbtr275czgqm">Best verkochte producten</caption>
                  <thead className="text-[12px] text-muted" data-id="element-3889" data-mp="1rai7ai961pqc">
                    <tr className="border-b border-line" data-id="element-3890" data-mp="259837uk91jd4">
                      <th scope="col" className="w-8 px-4 py-2 text-left font-normal sm:px-5" data-id="element-3891" data-mp="25aszppq4jcdm">
                        <span className="sr-only" data-id="element-3892" data-mp="208k2uia4rq68">Plaats</span>
                      </th>
                      <th scope="col" className="py-2 text-left font-normal" data-id="element-3893" data-mp="2e8lfsw3o6o22">Product</th>
                      <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3894" data-mp="2a79mogll7kp4">Stuks</th>
                      <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3895" data-mp="2nzzb0rol91pe">Omzet</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line" data-id="element-3896" data-mp="2j2pmrfcxqw35">
                    {report.topProducts.map((p, i) => <tr key={p.productId} data-id="element-3897" data-mp="2sysq2ojix0ro" data-mpi={i}>
                        <td className="px-4 py-2.5 tabular-nums text-muted sm:px-5" data-id="element-3898" data-mp="2t03rihphxwfm">{i + 1}</td>
                        <td className="py-2.5" data-id="element-3899" data-mp="3clnfa10hiqdi">
                          <Link to={`/be/producten/${p.productId}`} className="text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-3900" data-mp="3cirgybkq5zoj">
                            {p.name}
                          </Link>
                        </td>
                        <td className="px-2 py-2.5 text-right tabular-nums" data-id="element-3901" data-mp="37kpynsv4i4e4">{p.qty}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-ink sm:px-5" data-id="element-3902" data-mp="2sw3b53vce6bt">{money(p.revenueExVatCents)}</td>
                      </tr>)}
                  </tbody>
                </table>}
            </Panel>

            <div className="space-y-6" data-id="element-3903" data-mp="2jtu3qmvdet2n">
              <Panel title="Per btw-tarief" description={channel ? 'Alle kanalen samen, voor de aangifte' : undefined} data-id="element-3904" data-mp="2oui3py1kuk90">
                <Breakdown rows={report.byVatRate.map(r => ({
              key: String(r.vatRate),
              label: `${r.vatRate}%`,
              value: money(r.revenueExVatCents),
              meta: `btw ${money(r.vatCents)}`
            }))} data-id="element-3905" data-mp="29ysgjwkno12p" />
              </Panel>
              <Panel title="Per betaalmethode" description="Ontvangen incl. btw, min terugbetaald" data-id="element-3906" data-mp="2qbfcfbwfhpvs">
                <Breakdown rows={report.byPaymentMethod.map(m => ({
              key: m.method,
              label: receivedMethodLabel[m.method],
              value: money(m.amountCents)
            }))} data-id="element-3907" data-mp="2lg00wqlw862j" />
              </Panel>
            </div>
          </div>

          {report.stock && <StockPeriodPanel stock={report.stock} exporting={exporting} onExport={() => void exportCsv('voorraad')} data-id="element-3908" data-mp="26oheob2fxfaq" />}

          <LiabilitiesPanel on={to} data-id="element-3909" data-mp="22nxfz327y3mh" />

          {/* Alleen met een collega: uren naast haar factuur. Eigen maandkeuze, los van de periode hierboven. */}
          {hasColleagues && <StaffHoursPanel data-id="element-3910" data-mp="1so6koz5u3klc" />}

          <p className="text-[12px] leading-relaxed text-muted" data-id="element-3911" data-mp="1srm1c6f5p1ov">
            Een verkoop is een betaalde bestelling of een bon. Behandelingen staan niet bij "best verkocht". De btw per tarief gaat altijd over alle kanalen samen. Bedragen
            zoals de server ze rekent; de export gebruikt precies dezelfde cijfers, met een kolom Kanaal.
            {API_BASE === null && ' Dit zijn voorbeeldcijfers uit de demo.'}{' '}
            Wat er werkt (klanten, bezetting, bezoekers, bronnen) staat in{' '}
            <Link to={`${ADMIN_BASE}/inzicht?${query}`} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-3912" data-mp="1shee00qpjdj8">
              Marketing-inzicht
            </Link>
            .
          </p>
        </div>}
    </div>;
}
function Delta({
  current,
  previous,
  label
}: {
  current: number;
  previous: number;
  label: string;
}) {
  const pct = change(current, previous);
  if (pct === null) return <p className="mt-2 text-[13px] text-muted" data-id="element-3913" data-mp="19mpn2hzsj3n2">Geen omzet in de vorige periode ({label.replace('t.o.v. ', '')})</p>;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRightIcon : ArrowDownRightIcon;
  return <p className="mt-2 flex flex-wrap items-center gap-x-2 text-[13px] text-muted" data-id="element-3914" data-mp="h290s2sy9wld">
      <span className={`inline-flex items-center gap-1 ${up ? 'text-sage' : 'text-clay'}`} data-id="element-3915" data-mp="28l6jr9oqwuz7">
        <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-3916" data-mp="2dgc8eg9j8nqz" />
        <span className="sr-only" data-id="element-3917" data-mp="2iikzf72uhfqg">{up ? 'Meer' : 'Minder'}: </span>
        {changeLabel(pct)}
      </span>
      {label} ({money(previous)})
    </p>;
}
function Stat({
  label,
  value,
  hint
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return <div className="flex flex-col-reverse" data-id="element-3918" data-mp="1urfmazoah1ju">
      <dt className="mt-1 text-[12px] leading-snug text-muted" data-id="element-3919" data-mp="1usgyx8kydw3o">
        {label}
        {hint && <span className="block text-[11px]" data-id="element-3920" data-mp="1ys7udplfd0ej">{hint}</span>}
      </dt>
      <dd className="text-[16px] tabular-nums text-ink" data-id="element-3921" data-mp="23jr1fpdyd2ki">{value}</dd>
    </div>;
}
function Breakdown({
  rows
}: {
  rows: {
    key: string;
    label: string;
    value: string;
    meta?: string;
  }[];
}) {
  if (rows.length === 0) return <p className="text-[13px] text-muted" data-id="element-3922" data-mp="1b2wppx0yelv4">Niets in deze periode.</p>;
  return <dl className="space-y-2 text-[14px]" data-id="element-3923" data-mp="13jx7hup8b0dc">
      {rows.map(r => <div key={r.key} className="flex items-baseline justify-between gap-4" data-id="element-3924" data-mp="13i2wp97o8omz">
          <dt className="text-muted" data-id="element-3925" data-mp="ylm2bx9vpe1g">{r.label}</dt>
          <dd className="text-right tabular-nums text-ink" data-id="element-3926" data-mp="uizf69o5khal">
            {r.value}
            {r.meta && <span className="block text-[12px] text-muted" data-id="element-3927" data-mp="pko3dtearwpq">{r.meta}</span>}
          </dd>
        </div>)}
    </dl>;
}